import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import { SERVICES, PROJECTS, PROCESS } from "../mock";
import { ArrowLeft, ArrowRight, Check, ArrowUpRight } from "lucide-react";
import CtaPremium from "../components/CtaPremium";
import TestimonialsPremium from "../components/TestimonialsPremium";

export default function ServiceDetailPage() {
  useReveal();
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const gallery = PROJECTS.slice((idx * 3) % PROJECTS.length, ((idx * 3) % PROJECTS.length) + 3);
  const fallback = PROJECTS.slice(0, 3);
  const g = (gallery.length === 3 ? gallery : fallback);

  return (
    <div className="page-enter">
      {/* Custom hero — big number, split image */}
      <section className="relative bg-ink text-bone -mt-[78px] overflow-hidden">
        <div className="relative pt-32 pb-16 lg:pb-24">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <Link to="/services" className="inline-flex items-center gap-2 text-eyebrow text-amber mb-6">
                <ArrowLeft className="w-4 h-4" /> All services
              </Link>
              <div className="text-eyebrow text-bone/60 mb-6">— {service.tag}</div>
              <div className="flex items-start gap-6">
                <div className="font-display-tight text-amber text-7xl md:text-9xl leading-none opacity-90">{service.number}</div>
                <h1 className="font-display-tight text-5xl md:text-7xl lg:text-[8rem] leading-[0.88] text-bone text-balance">{service.title}</h1>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden img-wipe">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-bone py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7 rv">
            <div className="text-eyebrow text-amber mb-5">— Overview</div>
            <h2 className="font-display-tight text-4xl lg:text-6xl leading-[0.98] text-ink mb-8 text-balance">{service.blurb}</h2>
            <p className="text-ink-soft text-lg leading-relaxed">{service.long}</p>
          </div>
          <div className="lg:col-span-5 rv">
            <div className="border border-line p-8 bg-bone-2">
              <h3 className="font-display text-3xl text-ink mb-6">Included as standard</h3>
              <ul className="space-y-4">
                {service.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="h-6 w-6 bg-amber/20 text-amber flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" style={{ color: "var(--amber)" }} /></span>
                    <span className="text-ink/85">{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="mt-8 flex items-center justify-between bg-ink text-bone px-6 py-4 hover:bg-amber hover:text-ink transition-colors">
                <span className="text-eyebrow">Request a Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ink text-bone py-24 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="text-eyebrow text-amber mb-5 rv">— How we deliver it</div>
          <h2 className="font-display-tight text-4xl lg:text-6xl leading-[0.98] text-bone mb-14 rv">Our {service.short.toLowerCase()} process.</h2>
          <div className="grid md:grid-cols-4 gap-0 border-y border-line-light">
            {PROCESS.map((p) => (
              <div key={p.step} className="p-8 border-line-light md:border-l first:border-l-0 rv">
                <div className="font-display-tight text-amber text-5xl mb-5">{p.step}</div>
                <h3 className="font-display text-2xl text-bone mb-2">{p.title}</h3>
                <p className="text-bone/70 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-bone py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div className="rv">
              <div className="text-eyebrow text-amber mb-5">— Recent work</div>
              <h2 className="font-display-tight text-4xl lg:text-6xl leading-[0.98] text-ink text-balance">Selected <span className="italic font-display">{service.short.toLowerCase()}</span> projects.</h2>
            </div>
            <Link to="/projects" className="text-eyebrow link-line rv">See all work →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {g.map((p, i) => (
              <Link to="/projects" key={p.id} className="group relative overflow-hidden block rv" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={p.thumb} alt={p.title} className="img-zoom w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-bone">
                  <div className="text-eyebrow text-amber mb-1">{p.category}</div>
                  <h3 className="font-display text-2xl">{p.title}</h3>
                  <p className="text-sm text-bone/70">{p.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsPremium />

      {/* Prev/Next */}
      <section className="bg-bone-2">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-line">
          <Link to={`/services/${prev.slug}`} className="group py-12 flex items-center justify-between gap-4 hover:bg-bone transition-colors px-4">
            <ArrowLeft className="w-5 h-5 text-amber group-hover:-translate-x-1 transition-transform" />
            <div className="text-right">
              <div className="text-eyebrow text-ink/45 mb-1">Previous</div>
              <div className="font-display text-2xl text-ink">{prev.title}</div>
            </div>
          </Link>
          <Link to={`/services/${next.slug}`} className="group py-12 flex items-center justify-between gap-4 hover:bg-bone transition-colors px-4">
            <div>
              <div className="text-eyebrow text-ink/45 mb-1">Next</div>
              <div className="font-display text-2xl text-ink">{next.title}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <CtaPremium />
    </div>
  );
}
